import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Phone, Mail, UserCheck, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { PageHeader, Loading, Empty, TempBadge, Pill, Stamp } from "../components/kit";
import LeadScoreBadge from "../components/LeadScoreBadge";
import LastContact from "../components/LastContact";
import { Select } from "../components/form";
import { Button } from "../components/ui/button";
import ContactForm from "../components/ContactForm";
import { LEAD_TEMPERATURES, LEAD_STATUSES, fullName, titleCase } from "../lib/constants";
import { toast } from "sonner";

export default function Leads() {
  const [items, setItems] = useState(null);
  const [temp, setTemp] = useState("");
  const [status, setStatus] = useState("");
  const [assignee, setAssignee] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [team, setTeam] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const p = new URLSearchParams({ sort: "-lead_score", limit: "500", professional: "false" });
    if (temp) p.set("lead_temperature", temp);
    if (status) p.set("lead_status", status);
    if (assignee) p.set("assigned_to", assignee);
    const { data } = await api.get(`/contacts?${p.toString()}`);
    setItems(data.items);
    setSelected(new Set());
  }, [temp, status, assignee]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/auth/users").then(({ data }) => setTeam(data.filter((u) => u.active !== false))).catch(() => {}); }, []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items?.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((c) => c.id)));
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/bulk-assign", { ids: [...selected], assigned_to: target || null });
      toast.success(`${data.updated} lead${data.updated === 1 ? "" : "s"} ${target ? `assigned to ${data.assigned_to_name}` : "unassigned"}`);
      window.dispatchEvent(new Event("gas:data-changed"));
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const teamOpts = team.map((u) => ({ value: u.id, label: u.name }));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Leads" subtitle="Real-estate transaction leads only, prioritized by lead score and temperature — work the hottest first. Professionals and vendors are excluded." testid="leads-header"
        actions={<Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setShowForm(true)} data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1.5" />New Lead</Button>} />
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="w-56"><Select options={[{ value: "", label: "All temperatures" }, ...LEAD_TEMPERATURES.map((t) => ({ value: t.value, label: t.label }))]} value={temp} onChange={setTemp} testid="leads-filter-temp" /></div>
        <div className="w-56"><Select options={[{ value: "", label: "All statuses" }, ...LEAD_STATUSES.map((s) => ({ value: s, label: s }))]} value={status} onChange={setStatus} testid="leads-filter-status" /></div>
        <div className="w-56"><Select options={[{ value: "", label: "Any agent" }, ...teamOpts]} value={assignee} onChange={setAssignee} testid="leads-filter-agent" /></div>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-16 z-20 mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gold/60 bg-navy-800 text-white px-4 py-3 shadow-lg animate-fade-up" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{selected.size} lead{selected.size === 1 ? "" : "s"} selected</span>
          <div className="w-56"><Select options={[{ value: "", label: "Unassign" }, ...teamOpts]} value={target} onChange={setTarget} testid="bulk-agent" /></div>
          <Button size="sm" className="bg-gold text-navy-900 hover:bg-gold/90 font-semibold" disabled={busy} onClick={assign} data-testid="bulk-assign-btn"><UserCheck className="w-4 h-4 mr-1.5" />{target ? "Assign" : "Unassign"} {selected.size}</Button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-white/70 hover:text-white flex items-center gap-1 text-xs" data-testid="bulk-clear"><X className="w-3.5 h-3.5" />Clear</button>
        </div>
      )}

      {!items ? <Loading /> : items.length === 0 ? <Empty title="No leads" /> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#0B1F3A]" data-testid="select-all-leads" /></th>
                <th className="text-left px-3 py-3">Name</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Temp</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Score</th><th className="text-left px-4 py-3 hidden lg:table-cell">Agent</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Last contact</th><th className="text-left px-4 py-3 hidden xl:table-cell">Next follow-up</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(c.id) ? "bg-amber-50/60" : ""}`} onClick={() => nav(`/contacts/${c.id}`)} data-testid={`lead-row-${c.id}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="accent-[#0B1F3A]" data-testid={`select-lead-${c.id}`} /></td>
                  <td className="px-3 py-3 font-medium text-navy-800">{fullName(c)}<div className="text-[11px] text-slate-400 font-normal">{c.lead_source || ""}</div></td>
                  <td className="px-4 py-3"><Pill>{titleCase(c.contact_type || "prospect")}</Pill></td>
                  <td className="px-4 py-3"><TempBadge value={c.lead_temperature} /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><LeadScoreBadge contact={c} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600" data-testid={`lead-agent-${c.id}`}>{c.assigned_to_name || <span className="text-slate-300">Unassigned</span>}</td>
                  <td className="px-4 py-3 hidden lg:table-cell"><LastContact contact={c} testid={`lead-last-contact-${c.id}`} /></td>
                  <td className="px-4 py-3 text-slate-500 hidden xl:table-cell">{c.next_follow_up || "—"}</td>
                  <td className="px-4 py-3"><div className="flex gap-1.5 text-slate-400">{c.primary_phone && <a href={`tel:${c.primary_phone}`} onClick={(e) => e.stopPropagation()}><Phone className="w-4 h-4 hover:text-gold-dark" /></a>}{c.email && <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}><Mail className="w-4 h-4 hover:text-gold-dark" /></a>}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ContactForm open={showForm} onClose={() => setShowForm(false)} onSaved={load} defaultType="buyer" />
    </div>
  );
}
